<template>
  <div id="history" class="tab-content active">
    <div class="glass-card p-8">
      <div class="flex justify-between items-center mb-4">
        <h1 class="text-3xl font-bold text-gradient">历史记录</h1>
        <button @click="fetchRecords" class="btn-tertiary px-4 py-2 rounded-lg text-sm flex items-center">
          <i class="fas fa-sync-alt mr-2"></i>刷新
        </button>
      </div>
      <p class="text-gray-600 mb-6">这里是您过往一周的AIGC优化记录，方便您随时查看和管理。</p>

      <!-- 加载状态 -->
      <div v-if="isLoading" class="flex justify-center items-center h-64">
        <div class="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500"></div>
      </div>

      <!-- 空数据状态 -->
      <div v-else-if="records.length === 0" class="text-center py-16">
        <i class="fas fa-box-open text-4xl text-gray-400 mb-4"></i>
        <p class="text-gray-500">暂无处理记录</p>
        <button @click="fetchRecords" class="mt-4 btn-secondary px-4 py-2 rounded-lg text-sm">
          <i class="fas fa-redo-alt mr-2"></i>重试
        </button>
      </div>

      <!-- 历史记录列表 -->
      <div v-else class="space-y-4">
        <div v-for="record in records" :key="record.id" class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
          <div class="flex justify-between items-center">
            <div>
              <p class="text-sm text-gray-500">{{ formatDate(record.timestamp) }}</p>
              <p class="text-sm text-gray-400 mt-1">原文-{{ record.originalCharacterCount }}字符</p>
            </div>
            <div class="flex gap-2">
              <button @click="openModal(record, 'original')" class="btn-tertiary text-sm px-3 py-1 rounded-md">
                查看处理前
              </button>
              <button @click="openModal(record, 'processed')" class="btn-secondary text-sm px-3 py-1 rounded-md">
                <i class="fas fa-magic mr-1"></i>查看处理后
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 查看详情模态框 -->
    <div v-if="showModal" class="modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 active">
      <div class="glass-card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold text-purple-700">
            <i class="fas fa-file-alt mr-2"></i>{{ contentType === 'original' ? '处理前文案' : '处理后文案' }}
          </h3>
          <button @click="closeModal" class="text-gray-500 hover:text-gray-700">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body bg-white rounded-lg border border-gray-200 p-4">
          <div class="flex justify-end mb-2">
             <button 
              class="bg-gray-200 hover:bg-gray-300 px-3 py-1 rounded-lg text-sm"
              @click="copyToClipboard"
              :disabled="!currentRecord || (contentType === 'original' && !currentRecord.originalText) || (contentType === 'processed' && !currentRecord.processedText)"
            >
              <i class="fas fa-copy mr-1"></i>{{ copied ? '已复制!' : '复制' }}
            </button>
          </div>
          <p class="text-gray-700 whitespace-pre-wrap leading-relaxed">{{ contentType === 'original' ? (currentRecord?.originalText || '无内容') : (currentRecord?.processedText || '无内容') }}</p>
        </div>
         <div class="mt-4 flex justify-end">
           <button @click="closeModal" class="btn-secondary px-4 py-2 rounded-lg">
            <i class="fas fa-check mr-2"></i>完成
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { api } from '../utils/api';
import toast from '../utils/toast'

const records = ref([]);
const isLoading = ref(false);
const showModal = ref(false);
const currentRecord = ref(null);
const contentType = ref(''); // 'original' 或 'processed'
const copied = ref(false);

// 从API获取历史记录
const fetchRecords = async () => {
  isLoading.value = true;
  try {
    const data = await api.fetch('/api/aigc/record', {
      method: 'GET'
    });

    const transformedData = data.map(item => ({
      id: item.id,
      originalCharacterCount: item.textCount,
      originalText: '', // 初始化为空
      processedText: '', // 初始化为空
      timestamp: item.createTime
    })).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)); // 按时间降序排序

    records.value = transformedData;
  } catch (err) {
    // 错误已由api.js处理，这里只需要处理UI状态
    records.value = [];
    console.error('Error fetching records:', err);
  } finally {
    isLoading.value = false;
  }
};

// 初始化时加载记录
onMounted(() => {
  fetchRecords();
});

// 日期格式化函数
const formatDate = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const openModal = async (record, type) => {
  contentType.value = type;
  showModal.value = true;
  currentRecord.value = { ...record }; // 复制记录，避免响应式问题

  // 如果已经加载过，则不再请求
  const textKey = type === 'original' ? 'originalText' : 'processedText';
  if (currentRecord.value[textKey]) {
    return;
  }
  
  // 显示加载状态
  currentRecord.value[textKey] = '正在加载...';

  try {
    const viewType = type === 'original' ? 1 : 2;
    const data = await api.fetch(`/api/aigc/view?id=${record.id}&type=${viewType}`, {
      method: 'GET',
    });
    currentRecord.value[textKey] = data || '无内容';
  } catch (err) {
    currentRecord.value[textKey] = '加载失败，请重试。';
    console.error(`Error fetching ${type} text:`, err);
    toast.show('内容加载失败');
  }
};

const closeModal = () => {
  showModal.value = false;
  currentRecord.value = null;
  contentType.value = '';
  copied.value = false; // 关闭时重置复制状态
};

const copyToClipboard = async () => {
  try {
    const textToCopy = contentType.value === 'original' ? currentRecord.value.originalText : currentRecord.value.processedText;
    if (!textToCopy) return;

    await navigator.clipboard.writeText(textToCopy);
    copied.value = true;
    setTimeout(() => copied.value = false, 2000);
  } catch (err) {
    console.error('复制失败:', err);
    toast.show('复制失败，请手动复制');
  }
};
</script>

<style scoped>
/* 引入 DetectionView 和 ManualView 的通用样式理念 */
.btn-secondary {
    background-color: #8a2be2; /* 紫色 */
    color: white;
    transition: background-color 0.3s;
}
.btn-secondary:hover {
    background-color: #7b1fa2;
}
.btn-secondary:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
}

.btn-tertiary {
    background-color: #f3e5f5; /* 浅紫色 */
    color: #8a2be2;
    border: 1px solid #ce93d8;
    transition: background-color 0.3s, color 0.3s;
}

.btn-tertiary:hover {
    background-color: #e1bee7;
}

.text-gradient {
  background: -webkit-linear-gradient(45deg, #8a2be2, #4a00e0);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.glass-card {
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(10px);
  border-radius: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.modal.active {
  display: flex;
}

/* 覆盖一些Tailwind可能不直接提供的样式 */
.tab-content {
    padding: 2rem;
    min-height: 100vh;
}
</style>